import { EVENTS } from '@/plugins/analytics/events';
import { track } from '@/plugins/analytics';
import { useEncounterStore } from '@/stores/encounter';
import {
  getActiveTextModeContext,
  setActiveTextModeContext,
  updateActiveTextModeContext,
  type GuidedAnalyticsSession,
} from '@/features/text-mode/analytics/text-mode-context';

export const GUIDED_DEMOGRAPHICS_PANEL = 'demographics';
export const GUIDED_SYMPTOMS_PANEL = 'symptoms';
export const GUIDED_SUMMARY_PANEL = 'summary';

interface GuidedStep {
  panelName: string;
  stepIndex?: number;
  stepCount?: number;
  assessmentPath?: string | null;
}

interface StepViewOptions {
  revisit?: boolean;
}

function readSession(): GuidedAnalyticsSession | null {
  return getActiveTextModeContext()?.guidedSession ?? null;
}

function writeSession(patch: Partial<GuidedAnalyticsSession>): void {
  const current = readSession();
  if (!current) return;
  updateActiveTextModeContext({ guidedSession: { ...current, ...patch } });
}

function pausedMs(): number {
  try {
    return useEncounterStore().pausedDurationMs || 0;
  } catch {
    return 0;
  }
}

function secondsSince(startedAt: number | null | undefined, excludePaused = false): number | null {
  if (!startedAt) return null;
  const elapsed = Date.now() - startedAt - (excludePaused ? pausedMs() : 0);
  return Math.max(0, Math.round(elapsed / 1000));
}

function basePayload(): Record<string, unknown> {
  const context = getActiveTextModeContext();

  return {
    encounter_id: context?.encounterId ?? null,
    route_name: context?.routeName ?? null,
    event_timestamp: new Date().toISOString(),
  };
}

function setGuidedStep(step: GuidedStep): void {
  updateActiveTextModeContext({
    panelName: step.panelName,
    stepIndex: step.stepIndex,
    stepCount: step.stepCount,
    assessmentPath: step.assessmentPath ?? undefined,
  });
}

export function trackGuidedModeStarted(params: {
  encounterId: string | null;
  routeName?: string | null;
}): boolean {
  const existing = getActiveTextModeContext();
  if (existing?.mode === 'guided' && existing.encounterId === params.encounterId) return false;

  const now = Date.now();
  setActiveTextModeContext({
    ...params,
    mode: 'guided',
    panelName: GUIDED_DEMOGRAPHICS_PANEL,
    guidedSession: { startedAt: now, assessmentStartedAt: null, stepEnteredAt: now, editCount: 0 },
  });
  track(EVENTS.FLEXI_GUIDED_MODE_STARTED, basePayload());
  return true;
}

export function trackGuidedQuestionViewed(step: GuidedStep, options: StepViewOptions = {}): void {
  const secondsOnPreviousStep = options.revisit ? null : secondsSince(readSession()?.stepEnteredAt);
  writeSession({ stepEnteredAt: Date.now() });
  setGuidedStep(step);
  track(EVENTS.FLEXI_GUIDED_QUESTION_VIEWED, {
    ...basePayload(),
    seconds_on_previous_step: secondsOnPreviousStep,
    ...(options.revisit ? { revisit: true } : {}),
  });
}

export function trackGuidedAssessmentSelected(params: {
  assessmentPath: string;
  stepCount: number;
}): void {
  writeSession({ assessmentStartedAt: Date.now() });
  updateActiveTextModeContext({ assessmentPath: params.assessmentPath });
  track(EVENTS.FLEXI_GUIDED_ASSESSMENT_SELECTED, {
    ...basePayload(),
    assessment_path: params.assessmentPath,
    step_count: params.stepCount,
  });
}

export function trackGuidedQuestionAnswered(params: { unknownSelected: boolean }): void {
  track(EVENTS.FLEXI_GUIDED_QUESTION_ANSWERED, {
    ...basePayload(),
    unknown_selected: params.unknownSelected,
  });
}

export function trackGuidedAnswerEdited(editedPanelName: string): void {
  writeSession({ editCount: (readSession()?.editCount ?? 0) + 1 });
  track(EVENTS.FLEXI_GUIDED_ANSWER_EDITED, {
    ...basePayload(),
    edited_panel_name: editedPanelName,
  });
}

export function trackGuidedSummaryViewed(stepCount: number, options: StepViewOptions = {}): void {
  const secondsOnPreviousStep = options.revisit ? null : secondsSince(readSession()?.stepEnteredAt);
  writeSession({ stepEnteredAt: Date.now() });
  setGuidedStep({ panelName: GUIDED_SUMMARY_PANEL, stepCount });
  track(EVENTS.FLEXI_GUIDED_SUMMARY_VIEWED, {
    ...basePayload(),
    edit_count: readSession()?.editCount ?? 0,
    seconds_on_previous_step: secondsOnPreviousStep,
    ...(options.revisit ? { revisit: true } : {}),
  });
}

export function trackGuidedAssessmentCompleted(params: { questionsAnswered: number }): void {
  track(EVENTS.FLEXI_GUIDED_ASSESSMENT_COMPLETED, {
    ...basePayload(),
    questions_answered: params.questionsAnswered,
    edit_count: readSession()?.editCount ?? 0,
    duration_seconds_from_guided_start: secondsSince(readSession()?.startedAt, true),
    duration_seconds_from_assessment_start: secondsSince(readSession()?.assessmentStartedAt, true),
  });
}
